import React from "react";
import { mileageLabel, panelSpecs } from "@1-now/booking-core";
import { useData } from "data/data-provider";
import { specNumber, specText } from "utils/carSpecs";

interface VehicleDetailsSectionProps {
  data?: any;
}

const defaultData = {
  specs: {
    exteriorColor: "Lunar Blue",
    interiorColor: "Macchiato Beige Exclusive Nappa Leather",
    milesIncluded: 150,
    milesWhenInService: 20237,
    transmission: "Automatic",
    fuelType: "Premium Unleaded",
  },
};

const VehicleDetailsSection: React.FC<any> = ({ data = defaultData }) => {
  const { ourCompany } = useData();
  /** "4 seats", "1 seat": the package's spec labels, in this design's case. */
  const specLabel = (key: string) =>
    panelSpecs(data).find((spec) => spec.key === key)?.label.toLowerCase() ?? "";
  const vehicleData = {
    ...defaultData,
    ...data,

    specs: { ...defaultData.specs, ...(data?.specs || {}) },
  };
  const [showAll, setShowAll] = React.useState(false);

  // A spec with no value (null, 0, "N/A", a dash) is not shown, and the
  // Vehicle Information block goes when none has one (1NOW-11141).
  const color = specText(data?.color);
  const interiorColor = specText(data?.interior_color);
  const year = specNumber(data?.year);
  const seats = specNumber(data?.seats);
  const doors = specNumber(data?.doors);
  const transmission = specText(data?.transmission_type);
  const fuel = specText(data?.fuel_type);
  const miles = mileageLabel(data, ourCompany);
  const hasSpecs = !!(color || interiorColor || year || seats || doors || transmission || fuel || miles);

  return (
    <>
      {data?.description || hasSpecs || data?.features?.length > 0 ? (
        <>
          <section className=" text-text-color w-full flex flex-col rounded-md sm:border-b sm:border-t sm:border-l sm:border-r border-secondary-100  space-y-6 sm:space-y-8 pb-2 px-0 sm:p-4 xl:p-8 ">
            <div className="">
              {data?.description && (
                <div>
                  <h2 className="text-2xl  font-semibold mb-6">
                    General Description
                  </h2>
                  {(() => {
                    const desc = data?.description;
                    const charLimit = 450;
                    if (!desc) return null;
                    if (showAll || desc.length <= charLimit) {
                      return (
                        <>
                          <p className="text-text-color text-md leading-relaxed mb-4">
                            {desc}
                          </p>
                          {desc.length > charLimit && (
                            <button
                              className="font-bold underline mt-2"
                              onClick={() => setShowAll(false)}
                            >
                              VIEW LESS
                            </button>
                          )}
                        </>
                      );
                    } else {
                      return (
                        <>
                          <p className="text-text-color text-md leading-relaxed mb-4">
                            {desc.slice(0, charLimit)}...
                          </p>
                          <button
                            className="font-bold underline mt-2"
                            onClick={() => setShowAll(true)}
                          >
                            VIEW MORE
                          </button>
                        </>
                      );
                    }
                  })()}
                </div>
              )}
              {/* Vehicle Description */}
              {hasSpecs ? (
                <div className="mb-2">
                  <h2 className="text-2xl  font-semibold mb-6">
                    Vehicle Information
                  </h2>
                  <div className="grid grid-cols-1 gap-4">
                    {color && (
                      <div className="flex justify-between items-center pb-2 border-b border-secondary-100">
                        <span className="text-text-color text-sm font-semibold">
                          EXTERIOR COLOR
                        </span>
                        <span className="text-text-color font-semibold">
                          {color}
                        </span>
                      </div>
                    )}
                    {interiorColor && (
                      <div className="flex justify-between items-center pb-2 border-b border-secondary-100">
                        <span className="text-text-color text-sm font-semibold">
                          INTERIOR COLOR
                        </span>
                        <span className="text-text-color font-semibold text-right w-full sm:w-auto">
                          {interiorColor}
                        </span>
                      </div>
                    )}

                    {miles && (
                        <div className="flex justify-between items-center pb-2 border-b border-secondary-100">
                          <span className="text-text-color text-sm font-semibold">
                            MILES INCLUDED
                          </span>
                          <span className="text-text-color font-semibold">
                            {/* mileageLabel: the cap that exists, in the
                                operator's unit. This printed "null miles/trip"
                                for a car with only a daily cap. */}
                            {miles}
                          </span>
                        </div>
                      )}
                    {year && (
                      <div className="flex justify-between items-center pb-2 border-b border-secondary-100">
                        <span className="text-text-color text-sm font-semibold">
                          YEAR
                        </span>
                        <span className="text-text-color font-semibold">
                          {year}
                        </span>
                      </div>
                    )}
                    {seats && (
                      <div className="flex justify-between items-center pb-2 border-b border-secondary-100">
                        <span className="text-text-color text-sm font-semibold">
                          SEATING CAPACITY
                        </span>
                        <span className="text-text-color font-semibold">
                          {specLabel("seats")}
                        </span>
                      </div>
                    )}
                    {doors && (
                      <div className="flex justify-between items-center pb-2 border-b border-secondary-100">
                        <span className="text-text-color text-sm font-semibold">
                          NUMBER OF DOORS
                        </span>
                        <span className="text-text-color font-semibold">
                          {specLabel("doors")}
                        </span>
                      </div>
                    )}
                    {transmission && (
                      <div className="flex justify-between items-center pb-2 border-b border-secondary-100">
                        <span className="text-text-color text-sm font-semibold">
                          TRANSMISSION
                        </span>
                        <span className="text-text-color font-semibold">
                          {transmission}
                        </span>
                      </div>
                    )}
                    {fuel && (
                      <div className="flex justify-between items-center pb-2 border-b border-secondary-100">
                        <span className="text-text-color text-sm font-semibold">
                          FUEL TYPE
                        </span>
                        <span className="text-text-color font-semibold">
                          {fuel}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {data?.features && data.features.length > 0 && (
                <div>
                  <h2 className="text-2xl  font-semibold mb-6">
                    Additional Features:
                  </h2>
                  <ul className="list-disc pl-6 space-y-2">
                    {data?.features?.map((item: any, idx: number) => (
                      <li key={idx}>{item.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        </>
      ) : null}
    </>
  );
};

export default VehicleDetailsSection;
